import {onBeforeUnmount, watch, type Ref} from 'vue';
import {isRedirectingToLogout} from '@/api/apiClient';
import type {Announcement} from './types';

/** Never ask more often than this, however eager the server's hint. */
const FLOOR_MS = 3_000;
/** Used when the server offers no hint of its own. */
const DEFAULT_MS = 60_000;
/**
 * Deadline day is exactly when many people are waiting at once, and a fixed interval would
 * synchronise them into a herd arriving together.
 */
const JITTER_MS = 15_000;
/**
 * Bounds a single request. A connection that hangs without ever erroring would otherwise leave the
 * poller with no timer pending and nothing to reschedule it — stalled until the tab is hidden and
 * shown again.
 */
const REQUEST_TIMEOUT_MS = 15_000;

export function hasPending(announcements: Announcement[]): boolean {
    return announcements.some(a => a.state === 'pending');
}

/**
 * Drops pending announcements whose deadline has passed. The server filters these out on read too,
 * but a client that cannot reach the server would otherwise keep showing — and keep polling for —
 * an outcome that is never coming.
 */
export function withoutExpired(announcements: Announcement[], now: number): Announcement[] {
    return announcements.filter(a =>
        a.state !== 'pending' || !a.expiresAt || Date.parse(a.expiresAt) > now);
}

/**
 * How long to wait before asking again: the tightest spacing any pending announcement asks for,
 * clamped to the floor, plus jitter. A spacing rather than a deadline, so the server keeps pacing
 * the whole wait — an "ask after T" hint stops saying anything the moment T passes, leaving a long
 * wait to run at the floor. `random` is injectable so tests are not at the mercy of chance.
 */
export function nextDelayMs(
    announcements: Announcement[],
    random: () => number = Math.random,
): number {
    const spacings = announcements
        .filter(a => a.state === 'pending' && a.checkEverySeconds)
        .map(a => (a.checkEverySeconds as number) * 1_000)
        .filter(ms => Number.isFinite(ms) && ms > 0);

    const base = spacings.length ? Math.min(...spacings) : DEFAULT_MS;

    return Math.max(FLOOR_MS, base) + random() * JITTER_MS;
}

/**
 * Polls for outcomes, but only while this user is actually waiting for one — an announcement they
 * triggered themselves. With nothing pending, nothing runs: no timer, no requests. That is what
 * separates this from a per-user interval across the whole app, which would cost every user every
 * minute to deliver something that happens twice a week.
 */
export function useAnnouncementPoller(
    announcements: Ref<Announcement[]>,
    refresh: (signal: AbortSignal) => Promise<void>,
) {
    let timer: number | null = null;
    let ticking = false;

    function stop() {
        if (timer === null) return;
        window.clearTimeout(timer);
        timer = null;
    }

    function shouldRun(): boolean {
        // The session is gone and apiClient is already navigating away; polling on would just fire
        // 401s behind the redirect.
        if (isRedirectingToLogout()) return false;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
        return hasPending(announcements.value);
    }

    function schedule() {
        stop();
        if (!shouldRun()) return;
        timer = window.setTimeout(tick, nextDelayMs(announcements.value));
    }

    async function tick() {
        timer = null;
        if (ticking || !shouldRun()) return;

        ticking = true;
        const controller = new AbortController();
        const abortAt = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            await refresh(controller.signal);
        } catch {
            // Transient — try again on the next tick rather than giving up on the outcome.
        } finally {
            window.clearTimeout(abortAt);
            ticking = false;
        }

        schedule();
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'hidden') stop();
        else schedule();
    }

    watch(announcements, schedule, {immediate: true, deep: true});

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }

    onBeforeUnmount(() => {
        stop();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        }
    });

    return {stop};
}
